import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/AuthContext';
import { FirebaseSimple } from '../../utils/firebaseSimple';
import { doc, getDoc, collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  BookOpen, 
  MessageSquare, 
  Users,
  TrendingUp,
  Calendar,
  Target,
  Award,
  Clock,
  CheckCircle,
  Plus,
  ArrowRight,
  Zap,
  Shield,
  Star,
  Activity,
  Flame,
  Brain,
  AlertTriangle,
  WifiOff,
  CircleDot,
} from 'lucide-react';
import { format, subDays, startOfWeek } from 'date-fns';
import { Link } from 'react-router-dom';
import { getStreakData } from '../../utils/streakTracker';
import { WeekCalendar } from '../../components/user/WeekCalendar';
import { EntryDetailModal } from '../../components/user/JournalCalendar';
import { FirstTimeInstallModal } from '../../components/user/FirstTimeInstallModal';

export const ReliableDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [modules, setModules] = useState<any[]>([]);
  const [journals, setJournals] = useState<any[]>([]);
  const [promptedJournals, setPromptedJournals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [firebaseHealthy, setFirebaseHealthy] = useState<boolean | null>(true);
  const [hasAccessCode, setHasAccessCode] = useState<boolean | null>(null);
  const [streakData, setStreakData] = useState<any>(null);
  const [openMetric, setOpenMetric] = useState<null | 'mood' | 'streak' | 'training' | 'wellness'>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEntries, setSelectedEntries] = useState<any[]>([]);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showFullCalendar, setShowFullCalendar] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Refresh data when component comes into view
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setRefreshTrigger(prev => prev + 1);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (!user) return;
    
    const loadData = async () => {
      setLoading(true);
      
      try {
        // Check if user has access code
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.accessCodeId) {
            // User has access code, verify it's still valid
            const accessCodeDoc = await getDoc(doc(db, 'accessCodes', userData.accessCodeId));
            if (accessCodeDoc.exists()) {
              const accessCodeData = accessCodeDoc.data();
              if (accessCodeData.isActive && 
                  (!accessCodeData.expiresAt || accessCodeData.expiresAt.toDate() > new Date())) {
                setHasAccessCode(true);
              } else {
                setHasAccessCode(false);
              }
            } else {
              setHasAccessCode(false);
            }
          } else {
            setHasAccessCode(false);
          }
        } else {
          setHasAccessCode(false);
        }

        // Try to load real data with timeout
        const dataPromise = Promise.all([
          FirebaseSimple.getModules(),
          FirebaseSimple.getUserJournals(user.uid),
          getStreakData(user.uid),
          // Fetch prompted journals for WeekCalendar
          (async () => {
            try {
              const entriesQuery = query(
                collection(db, 'users', user.uid, 'promptedJournals'),
                orderBy('completedAt', 'desc')
              );
              const entriesSnap = await getDocs(entriesQuery);
              return entriesSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                completedAt: doc.data().completedAt?.toDate(),
              }));
            } catch (error) {
              console.error('Error fetching prompted journals:', error);
              return [];
            }
          })()
        ]);
        
        const timeoutPromise = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 5000)
        );
        
        const [modulesData, journalsData, streakDataResult, promptedJournalsData] = await Promise.race([dataPromise, timeoutPromise]);
        
        setModules(modulesData);
        setJournals(journalsData);
        setPromptedJournals(promptedJournalsData || []);
        setStreakData(streakDataResult);
        setFirebaseHealthy(true);
        
      } catch (error) {
        console.warn('Failed to load Firebase data, using offline mode:', error);
        setFirebaseHealthy(false);
        // Use empty arrays - the UI will show appropriate messaging
        setModules([]);
        setJournals([]);
        setPromptedJournals([]);
      }
      
      setLoading(false);
    };

    loadData();
  }, [user, refreshTrigger]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getMoodEmoji = (mood: number) => {
    if (mood >= 8) return '😊';
    if (mood >= 6) return '🙂';
    if (mood >= 4) return '😐';
    if (mood >= 2) return '😟';
    return '😢';
  };

  // Calculate stats from actual data - combine regular journals and prompted journals
  const thisWeekStart = startOfWeek(new Date());
  
  // Combine all journals (regular + prompted with overallMood mapped to mood)
  const allJournals = [
    ...journals,
    ...promptedJournals.map(pj => ({
      ...pj,
      mood: pj.overallMood || pj.mood || 0,
      createdAt: pj.completedAt || pj.createdAt
    }))
  ];
  
  const weeklyJournals = allJournals.filter(j => 
    j.createdAt && j.createdAt >= thisWeekStart
  );

  const avgMood = weeklyJournals.length > 0 
    ? weeklyJournals.reduce((sum, j) => sum + (j.mood || 0), 0) / weeklyJournals.length 
    : 0; // Changed default from 7.2 to 0

  const wellnessScore = avgMood > 0 
    ? Math.round(avgMood * 10)
    : 0; // Changed default from 72 to 0

  const stats = {
    streakDays: streakData?.currentStreak || 0,
    avgMood: avgMood > 0 ? avgMood : 7.2, // Display 7.2 if no data, but calculated from 0
    wellnessScore: wellnessScore > 0 ? wellnessScore : 72, // Display 72 if no data
    modulesAvailable: modules.length || 20,
    completedModules: 2 // This would come from user progress data
  };

  // Note: Removed automatic redirect to training-access
  // Users can now use the app without access codes
  // Only Training and Achievements require access codes

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-2xl animate-float mb-6">
            <Heart className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Loading Your Dashboard</h2>
          <p className="text-gray-600">Connecting to your wellness data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* First-time PWA Install Prompt */}
      <FirstTimeInstallModal />
      
      <div className="px-4 sm:px-6 lg:px-8 space-y-6 animate-fadeIn">
        {/* Connection Status */}
        {firebaseHealthy === false && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center space-x-3">
            <WifiOff className="w-5 h-5 text-yellow-600" />
            <div>
              <h3 className="font-medium text-yellow-900">Offline Mode</h3>
              <p className="text-yellow-800 text-sm">
                Some features may be limited. Your data will sync when connection is restored.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Welcome Section */}
      <div className="relative isolate overflow-hidden bg-gradient-to-br from-red-600 via-red-700 to-orange-600 rounded-2xl p-5 md:p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute top-2 right-2 w-64 h-64 bg-white opacity-5 rounded-full"></div>
        <div className="absolute bottom-2 left-2 w-48 h-48 bg-white opacity-5 rounded-full"></div>
        
        <div className="relative z-10">
          <div className="mb-4 md:mb-6">
            <div className="flex items-center justify-between md:mb-2">
              <h1 className="text-2xl md:text-3xl font-bold animate-slideInLeft">
                {getGreeting()}, {user?.displayName || 'Hero'}! 👋
              </h1>
              {/* Time - Desktop Only */}
              <div className="hidden md:block text-right animate-slideInRight">
                <div className="text-2xl font-bold">{format(currentTime, 'h:mm')}</div>
                <div className="text-red-200 text-sm">{format(currentTime, 'a')}</div>
              </div>
            </div>
            {/* Date/Role - Desktop Only */}
            <p className="hidden md:block text-red-100 text-lg animate-slideInLeft animation-delay-200">
              {user?.responderType} • {format(currentTime, 'EEEE, MMMM d')}
            </p>
          </div>
          
          <div className="grid grid-cols-3 md:grid-cols-3 gap-2 md:gap-4">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white border-opacity-20">
              <div className="flex flex-col md:flex-row items-center md:space-x-3 text-center md:text-left">
                <div className="bg-white bg-opacity-30 p-1.5 md:p-2 rounded-lg mb-1 md:mb-0">
                  <Flame className="w-4 h-4 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <div className="text-xl md:text-2xl font-bold">{stats.streakDays}</div>
                  <div className="text-red-100 text-xs md:text-sm">Day Streak</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white border-opacity-20">
              <div className="flex flex-col md:flex-row items-center md:space-x-3 text-center md:text-left">
                <div className="bg-white bg-opacity-30 p-1.5 md:p-2 rounded-lg mb-1 md:mb-0">
                  <Heart className="w-4 h-4 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <div className="text-xl md:text-2xl font-bold">{stats.avgMood.toFixed(1)}</div>
                  <div className="text-red-100 text-xs md:text-sm">Avg Mood</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-3 md:p-4 border border-white border-opacity-20">
              <div className="flex flex-col md:flex-row items-center md:space-x-3 text-center md:text-left">
                <div className="bg-white bg-opacity-30 p-1.5 md:p-2 rounded-lg mb-1 md:mb-0">
                  <Target className="w-4 h-4 md:w-6 md:h-6 text-white" />
                </div>
                <div>
                  <div className="text-xl md:text-2xl font-bold">{stats.wellnessScore}</div>
                  <div className="text-red-100 text-xs md:text-sm">Wellness Score</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions - Mobile Optimized */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-7xl mx-auto">
        {/* Journal */}
        <Link
          to="/journal"
          className="group relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="bg-white bg-opacity-20 p-2 md:p-3 rounded-lg md:rounded-xl">
                <MessageSquare className="w-6 h-6 md:w-8 md:h-8" />
              </div>
              <ArrowRight className="w-5 h-5 md:w-6 md:h-6 opacity-60 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all duration-300" />
            </div>
            <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-2">Journal</h3>
            <p className="text-blue-100 text-xs md:text-sm">Prime your brain to focus on the good stuff</p>
          </div>
          <div className="absolute -bottom-2 -right-2 md:-bottom-4 md:-right-4 w-16 h-16 md:w-24 md:h-24 bg-white opacity-5 rounded-full"></div>
        </Link>

        {/* Meditations */}
        <Link
          to="/meditations"
          className="group relative overflow-hidden bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="bg-white bg-opacity-20 p-2 md:p-3 rounded-lg md:rounded-xl">
                <Heart className="w-6 h-6 md:w-8 md:h-8" />
              </div>
              <ArrowRight className="w-5 h-5 md:w-6 md:h-6 opacity-60 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all duration-300" />
            </div>
            <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-2">Meditations</h3>
            <p className="text-indigo-100 text-xs md:text-sm">Minimize negative thought patterns & regulate your nervous system</p>
          </div>
          <div className="absolute -bottom-2 -right-2 md:-bottom-4 md:-right-4 w-16 h-16 md:w-24 md:h-24 bg-white opacity-5 rounded-full"></div>
        </Link>

        {/* GRIPS */}
        <Link
          to="/grips"
          className="group relative overflow-hidden bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="bg-white bg-opacity-20 p-2 md:p-3 rounded-lg md:rounded-xl">
                <MessageSquare className="w-6 h-6 md:w-8 md:h-8" />
              </div>
              <ArrowRight className="w-5 h-5 md:w-6 md:h-6 opacity-60 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all duration-300" />
            </div>
            <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-2">G.R.I.P.S.</h3>
            <p className="text-cyan-100 text-xs md:text-sm">Your Grit & Resilience Integrated Peer Support</p>
          </div>
          <div className="absolute -bottom-2 -right-2 md:-bottom-4 md:-right-4 w-16 h-16 md:w-24 md:h-24 bg-white opacity-5 rounded-full"></div>
        </Link>

        {/* Training Videos */}
        <Link
          to="/training"
          className="group relative overflow-hidden bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="bg-white bg-opacity-20 p-2 md:p-3 rounded-lg md:rounded-xl">
                <BookOpen className="w-6 h-6 md:w-8 md:h-8" />
              </div>
              <ArrowRight className="w-5 h-5 md:w-6 md:h-6 opacity-60 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all duration-300" />
            </div>
            <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-2">Training Videos</h3>
            <p className="text-green-100 text-xs md:text-sm">Access evidence-based training modules</p>
          </div>
          <div className="absolute -bottom-2 -right-2 md:-bottom-4 md:-right-4 w-16 h-16 md:w-24 md:h-24 bg-white opacity-5 rounded-full"></div>
        </Link>

        {/* Emotions Wheel */}
        <button
          onClick={() => window.open('/Emotions Wheel.pdf', '_blank')}
          className="group relative overflow-hidden bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 text-left w-full"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-indigo-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="bg-white bg-opacity-20 p-2 md:p-3 rounded-lg md:rounded-xl">
                <CircleDot className="w-6 h-6 md:w-8 md:h-8" />
              </div>
              <ArrowRight className="w-5 h-5 md:w-6 md:h-6 opacity-60 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all duration-300" />
            </div>
            <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-2">Emotions Wheel</h3>
            <p className="text-purple-100 text-xs md:text-sm">Identify and understand your emotions</p>
          </div>
          <div className="absolute -bottom-2 -right-2 md:-bottom-4 md:-right-4 w-16 h-16 md:w-24 md:h-24 bg-white opacity-5 rounded-full"></div>
        </button>

        {/* Post-Incident Check-in */}
        <Link
          to="/incident-checkin"
          className="group relative overflow-hidden bg-gradient-to-br from-orange-500 via-red-600 to-red-700 rounded-xl md:rounded-2xl p-4 md:p-6 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-red-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="bg-white bg-opacity-20 p-2 md:p-3 rounded-lg md:rounded-xl">
                <Shield className="w-6 h-6 md:w-8 md:h-8" />
              </div>
              <ArrowRight className="w-5 h-5 md:w-6 md:h-6 opacity-60 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all duration-300" />
            </div>
            <div className="flex items-center space-x-2 mb-1 md:mb-2">
              <AlertTriangle className="w-4 h-4 md:w-5 md:h-5" />
              <h3 className="text-lg md:text-xl font-bold">Post-Incident Check-in</h3>
            </div>
            <p className="text-red-100 text-xs md:text-sm">Just responded to a bad call? Let's assess how you're doing and get you support.</p>
          </div>
          <div className="absolute -bottom-2 -right-2 md:-bottom-4 md:-right-4 w-16 h-16 md:w-24 md:h-24 bg-white opacity-5 rounded-full"></div>
        </Link>

      </div>

      {/* Reflection History - Mobile Only */}
      <div className="md:hidden">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Reflection History
          </h2>
          <WeekCalendar
            entries={promptedJournals.map(j => ({
              id: j.id,
              promptType: j.promptType || (j.type === 'morning' ? 'morning' : 'evening'),
              completedAt: j.completedAt || j.createdAt,
              reflectionDetails: j.reflectionDetails
            }))}
            onDateSelect={(date, dateEntries) => {
              setSelectedDate(date);
              setSelectedEntries(dateEntries);
              setShowDateModal(true);
            }}
            onShowFullCalendar={() => setShowFullCalendar(true)}
          />
        </div>
      </div>

      {/* Enhanced Weekly Progress Calendar - Desktop Only */}
      <div className="hidden md:block bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-xl border border-blue-100 p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">This Week's Journey</h2>
            <p className="text-sm text-gray-600">Track your daily wellness progress</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`px-3 py-1 rounded-full ${
              stats.streakDays === 7 
                ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white' 
                : stats.streakDays >= 5 
                ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white'
                : 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white'
            }`}>
              <span className="text-sm font-bold">{stats.streakDays}/7 days</span>
            </div>
          </div>
        </div>
        
        {/* Enhanced Daily Progress Grid */}
        <div className="grid grid-cols-7 gap-2 md:gap-4 mb-6">
          {Array.from({ length: 7 }, (_, i) => {
            const date = new Date(thisWeekStart);
            date.setDate(date.getDate() + i);
            const dayJournals = journals.filter(j => 
              j.createdAt && format(j.createdAt, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
            );
            const hasEntry = dayJournals.length > 0 || i < stats.streakDays;
            const isToday = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
            const isPast = date < new Date() && !hasEntry;
            const isFuture = date > new Date() && !isToday;
            
            return (
              <button
                key={i}
                onClick={() => {
                  if (hasEntry && dayJournals.length > 0) {
                    setSelectedDate(date);
                    setShowDateModal(true);
                  } else if (isToday) {
                    navigate('/journal');
                  }
                }}
                disabled={!hasEntry && !isToday}
                className="text-center group focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-xl disabled:cursor-not-allowed"
              >
                <p className="text-xs md:text-sm text-gray-500 mb-2 font-medium">
                  {format(date, 'EEE')}
                </p>
                <div className={`relative w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center mx-auto transition-all duration-300 ${
                  hasEntry 
                    ? 'bg-gradient-to-br from-green-400 to-emerald-500 text-white shadow-lg transform scale-105 group-hover:scale-110 group-hover:shadow-xl cursor-pointer' 
                    : isToday
                    ? 'bg-gradient-to-br from-blue-400 to-blue-500 text-white shadow-md animate-pulse group-hover:scale-110 cursor-pointer'
                    : isPast
                    ? 'bg-gray-200 text-gray-400'
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  {hasEntry ? (
                    <CheckCircle className="w-5 h-5 md:w-6 md:h-6" />
                  ) : (
                    <span className="font-bold text-sm md:text-base">{format(date, 'd')}</span>
                  )}
                  {isToday && !hasEntry && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 md:w-3 md:h-3 bg-yellow-400 rounded-full animate-ping"></div>
                  )}
                  {hasEntry && dayJournals.length > 0 && (
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 flex gap-0.5">
                      {dayJournals.map((_, idx) => (
                        <div key={idx} className="w-1 h-1 bg-white rounded-full"></div>
                      ))}
                    </div>
                  )}
                </div>
                {hasEntry && (
                  <div className="mt-1 text-xs text-green-600 font-medium">✓</div>
                )}
              </button>
            );
          })}
        </div>
        
        {/* Enhanced Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Weekly Progress</span>
            <span className="text-sm font-bold text-gray-900">{Math.round((stats.streakDays / 7) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-1000 ease-out ${
                stats.streakDays === 7 
                  ? 'bg-gradient-to-r from-green-400 to-emerald-500' 
                  : stats.streakDays >= 5 
                  ? 'bg-gradient-to-r from-blue-400 to-blue-500'
                  : 'bg-gradient-to-r from-yellow-400 to-orange-500'
              }`}
              style={{ width: `${(stats.streakDays / 7) * 100}%` }}
            ></div>
          </div>
        </div>
        
        {/* Enhanced Call to Action */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 md:p-6 border border-red-100">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4">
            <div className="text-center md:text-left">
              <div className="text-sm md:text-base font-semibold text-gray-800 mb-1">
                {stats.streakDays === 7 ? (
                  <>
                    🎉 <span className="text-green-600">Perfect Week!</span> Keep the momentum going!
                  </>
                ) : stats.streakDays >= 5 ? (
                  <>
                    🔥 <span className="text-blue-600">Great Progress!</span> Almost there!
                  </>
                ) : stats.streakDays >= 3 ? (
                  <>
                    💪 <span className="text-yellow-600">Building Momentum!</span> Keep it up!
                  </>
                ) : (
                  <>
                    🚀 <span className="text-orange-600">Start Strong!</span> Every day counts!
                  </>
                )}
              </div>
              <p className="text-xs md:text-sm text-gray-600">
                {stats.streakDays === 7 
                  ? "You've completed a full week of wellness check-ins!" 
                  : `${7 - stats.streakDays} more days to complete this week`
                }
              </p>
            </div>
            <Link 
              to="/journal/morning"
              className={`px-4 md:px-6 py-2 md:py-3 rounded-lg text-white font-semibold transition-all duration-300 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                stats.streakDays === 7 
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700' 
                  : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
              }`}
            >
              <Plus className="w-4 h-4 md:w-5 md:h-5" />
              <span className="text-sm md:text-base">
                {stats.streakDays === 7 ? 'Continue Journey' : 'Check In Now'}
              </span>
            </Link>
          </div>
        </div>
      </div>

      {/* Achievement System */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Achievements</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { name: 'First Steps', icon: Shield, earned: true, color: 'from-blue-500 to-blue-600' },
            { name: 'Week Warrior', icon: Flame, earned: stats.streakDays >= 7, color: 'from-orange-500 to-red-500' },
            { name: 'Quick Learner', icon: Zap, earned: stats.completedModules >= 3, color: 'from-yellow-500 to-orange-500' },
            { name: 'Wellness Master', icon: Award, earned: stats.completedModules >= 10, color: 'from-purple-500 to-pink-500' }
          ].map((badge, index) => (
            <div
              key={badge.name}
              className={`text-center p-6 rounded-xl border-2 transition-all duration-500 hover:scale-105 ${
                badge.earned 
                  ? `border-transparent bg-gradient-to-br ${badge.color} text-white shadow-xl animate-glow` 
                  : 'border-gray-200 bg-gray-50 text-gray-400'
              }`}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                badge.earned ? 'bg-white bg-opacity-20 shadow-lg' : 'bg-gray-200'
              }`}>
                <badge.icon className="w-8 h-8" />
              </div>
              <div className="font-bold text-sm mb-1">{badge.name}</div>
              {badge.earned && (
                <div className="mt-2">
                  <div className="bg-white bg-opacity-20 text-xs px-2 py-1 rounded-full">
                    ✨ Earned!
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Motivational Section */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl p-8 text-white shadow-2xl relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full -translate-y-48 translate-x-48"></div>
        
        <div className="relative z-10 text-center">
          <div className="bg-white bg-opacity-20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">🏄</span>
          </div>
          <blockquote className="text-xl md:text-2xl font-light mb-4 leading-relaxed">
            "You can't control the waves, but you can learn how to surf."
          </blockquote>
          <p className="text-indigo-200">— Jon Kabat-Zinn</p>
        </div>
      </div>

      {/* Entry Detail Modal */}
      {showDateModal && selectedDate && selectedEntries.length > 0 && (
        <EntryDetailModal
          date={selectedDate}
          entries={selectedEntries}
          onClose={() => {
            setShowDateModal(false);
            setSelectedDate(null);
            setSelectedEntries([]);
          }}
        />
      )}

      {/* Legacy Date Modal - keeping for fallback if no selectedEntries */}
      {showDateModal && selectedDate && selectedEntries.length === 0 && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDateModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-1">
                    {format(selectedDate, 'EEEE, MMMM d')}
                  </h3>
                  <p className="text-blue-100 text-sm">Your wellness entries</p>
                </div>
                <button
                  onClick={() => setShowDateModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
                >
                  <Plus className="w-6 h-6 transform rotate-45" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
              {journals
                .filter(j => j.createdAt && format(j.createdAt, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd'))
                .map((journal, idx) => (
                  <div key={idx} className="mb-6 last:mb-0">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200">
                      {/* Entry Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2 rounded-lg">
                            <MessageSquare className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {journal.type === 'morning' ? 'Morning' : 'Evening'} Reflection
                            </p>
                            <p className="text-xs text-gray-500">
                              {journal.createdAt ? format(journal.createdAt, 'h:mm a') : ''}
                            </p>
                          </div>
                        </div>
                        {journal.mood && (
                          <div className="flex items-center space-x-2 bg-white px-3 py-1.5 rounded-full border border-gray-200">
                            <Heart className="w-4 h-4 text-pink-500" />
                            <span className="text-sm font-bold text-gray-900">{journal.mood}/10</span>
                          </div>
                        )}
                      </div>

                      {/* Entry Content */}
                      <div className="space-y-3">
                        {journal.gratitude && journal.gratitude.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Grateful For</p>
                            <ul className="space-y-1">
                              {journal.gratitude.map((item: string, i: number) => (
                                <li key={i} className="text-sm text-gray-700 flex items-start">
                                  <span className="text-green-500 mr-2">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {journal.goals && journal.goals.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Goals</p>
                            <ul className="space-y-1">
                              {journal.goals.map((item: string, i: number) => (
                                <li key={i} className="text-sm text-gray-700 flex items-start">
                                  <span className="text-blue-500 mr-2">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {journal.intention && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Intention</p>
                            <p className="text-sm text-gray-700">{journal.intention}</p>
                          </div>
                        )}
                        {journal.wins && journal.wins.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Wins</p>
                            <ul className="space-y-1">
                              {journal.wins.map((item: string, i: number) => (
                                <li key={i} className="text-sm text-gray-700 flex items-start">
                                  <span className="text-yellow-500 mr-2">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {journal.improvements && journal.improvements.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Improvements</p>
                            <ul className="space-y-1">
                              {journal.improvements.map((item: string, i: number) => (
                                <li key={i} className="text-sm text-gray-700 flex items-start">
                                  <span className="text-purple-500 mr-2">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {journal.reflection && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Reflection</p>
                            <p className="text-sm text-gray-700">{journal.reflection}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};
